/**
 * FTUE host (PEL-516).
 *
 * Single mount point (rendered once in the app layout). Reads the resolved
 * active step from the engine and renders the appropriate surface — coachmark
 * (+ spotlight), off-page badge, or toast — into a body portal. Renders nothing
 * when there is no active step or while waiting on an anchor (`surface: none`).
 */

import { type ReactNode, useEffect, useLayoutEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { allowsAnchorInteraction, isFtueInteractionAllowed } from '../interaction-policy'
import { useFtue } from '../use-ftue'
import { Coachmark } from './Coachmark'
import { FtueBadge } from './FtueBadge'
import { FtueToast } from './FtueToast'
import { Spotlight } from './Spotlight'

export function FtueHost() {
  const active = useFtue()

  // Diagnosis aid (spec §7): expose the resolved step + anchor element so a wrong
  // or stale anchor is visible from the console (`window.__ftue`). Keyed on the
  // step/element/surface so it doesn't churn on the per-frame `active` identity.
  const stepId = active?.step.id ?? null
  const anchorId = active?.step.anchorId ?? null
  const element = active?.element ?? null
  const surface = active?.surface ?? null
  const activeRef = useRef(active)
  activeRef.current = active
  useEffect(() => {
    if (!import.meta.env.DEV) return
    ;(window as unknown as { __ftue?: unknown }).__ftue = stepId
      ? {
          stepId,
          anchorId,
          surface,
          element,
          rect: element?.getBoundingClientRect() ?? null,
        }
      : null
  }, [stepId, anchorId, element, surface])

  // Some non-blocking journey steps are brief orientation moments rather than
  // actions. Advance those only after the coachmark is genuinely on screen;
  // waiting for a route/anchor must never consume a persisted milestone.
  const autoCompleteMs = active?.step.autoCompleteMs
  useEffect(() => {
    if (!stepId || surface !== 'coachmark' || !element || !autoCompleteMs) return
    const timer = window.setTimeout(() => activeRef.current?.onComplete(), autoCompleteMs)
    return () => window.clearTimeout(timer)
  }, [stepId, surface, element, autoCompleteMs])

  // Flag the document while a coaching step is on screen, so portaled Radix
  // popovers (Select/Dropdown/Popover) opened from the highlighted field are lifted
  // above the scrim instead of hiding behind the mask (see index.css). Only set for
  // controls owned by the active target can sit between the scrim and coachmark.
  const modalActive = !!active && active.surface === 'coachmark' && active.element != null
  useEffect(() => {
    if (!modalActive) return
    const root = document.documentElement
    root.setAttribute('data-ftue-modal', '')
    return () => root.removeAttribute('data-ftue-modal')
  }, [modalActive])

  // Geometry-based blockers provide the expected pointer cursor/hit target, but
  // a window capture guard is the authority. It runs before React/root handlers
  // and also catches keyboard-generated clicks, form submits, middle/right
  // clicks, drag/drop, and app overlays whose z-index happens to sit above the
  // spotlight. Only coachmark controls, the explicitly interactive anchor, and
  // a popup owned by that anchor are allowed.
  useLayoutEffect(() => {
    if (!modalActive || !activeRef.current?.element) return
    const guardedEvents = [
      'pointerdown',
      'pointerup',
      'mousedown',
      'mouseup',
      'touchend',
      'click',
      'auxclick',
      'contextmenu',
      'dragstart',
      'drop',
      'submit',
      'keydown',
      'keyup',
    ] as const
    const blockUnlessAllowed = (event: Event) => {
      if (
        event instanceof KeyboardEvent &&
        event.key !== 'Enter' &&
        event.key !== ' ' &&
        event.key !== 'Spacebar'
      ) {
        return
      }
      const current = activeRef.current
      if (
        !current ||
        current.surface !== 'coachmark' ||
        !current.element ||
        isFtueInteractionAllowed(event, current.step, current.element)
      ) {
        return
      }
      if (event.cancelable) event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
    }
    for (const type of guardedEvents) window.addEventListener(type, blockUnlessAllowed, true)
    return () => {
      for (const type of guardedEvents) window.removeEventListener(type, blockUnlessAllowed, true)
    }
  }, [modalActive])

  if (!active || active.surface === 'none') return null

  let node: ReactNode = null
  switch (active.surface) {
    case 'coachmark':
      node = active.element ? (
        <>
          {/* The scrim blocks interaction except coachmark controls and an anchor
              explicitly declared interactive by the active step. The
              Spotlight only mounts here when a coachmark is rendered on a present
              anchor, so waiting/event-gated states never show a scrim. A
              scroll-locked overlay step (e.g. the Match Analysis fit step) also
              clips the cutout to its scroll viewport so the highlight can't bleed
              past the scroll boundary into a pinned footer on a separate layer. */}
          <Spotlight
            element={active.element}
            allowAnchorInteraction={allowsAnchorInteraction(active.step)}
            clipToScrollParent={(active.step.lockScroll ?? false) && active.layer === 'overlay'}
            scrollPassthrough={!(active.step.lockScroll ?? false)}
            {...(active.step.spotlightPadding !== undefined
              ? { padding: active.step.spotlightPadding }
              : {})}
          />
          {/* Remount when the anchored element changes — by step AND by layer: a
              reused coachmark keeps floating-ui's autoUpdate bound to the previous
              reference, so the popover would freeze at the old anchor while only the
              spotlight re-resolves. The layer flips when a concept-based step
              re-anchors from the list card to the detail overlay (and back), so it
              must be part of the key. A fresh instance re-binds to the current
              element (and re-types the surface-specific copy). */}
          <Coachmark key={`${active.step.id}:${active.layer}`} active={active} />
        </>
      ) : null
      break
    case 'badge':
      node = <FtueBadge active={active} />
      break
    case 'toast':
      node = <FtueToast active={active} />
      break
  }

  if (!node) return null
  return createPortal(node, document.body)
}
